import React from 'react';
import { cn } from 'cn';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import Info from 'src/componentes/info';

type PanelProps = Omit<React.ComponentPropsWithoutRef<'div'>, 'title'> & {
  /** Título do card. Sem ele, o card não tem cabeçalho. */
  header?: React.ReactNode;
  /** Texto menor abaixo do título. */
  subtitle?: React.ReactNode;
  /** Conteúdo extra no cabeçalho, abaixo do título (ex.: filtros). */
  headerContent?: React.ReactNode;
  /** Conteúdo à direita do título, na mesma linha (ex.: botões). */
  headerAside?: React.ReactNode;
  /** Clique na linha do título (só com `headerAside`). Cliques em ícone de ajuda são ignorados. */
  onHeaderRowClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  /** Mostra o ícone "i" de ajuda ao lado do título, com `infoText`. */
  showInfo?: boolean;
  /** Texto da ajuda do `showInfo`. */
  infoText?: React.ReactNode;
  /** Id do ícone de ajuda. */
  idInfo?: string;
  /** Rodapé do card (ex.: botões de ação). Pode ser uma função que devolve o rodapé. */
  footer?: React.ReactNode | (() => React.ReactNode);
  /** Margem abaixo do card, em px. */
  marginBottomCard?: number | string;
  /** Largura do card. Use no lugar de `style.width`, que é sempre substituída por esta. */
  cardWidth?: number | string;
  /** Classes do corpo. */
  classBody?: string;
  /** Classes do rodapé. */
  classFooter?: string;
  /** Estilo do título. */
  headerStyle?: React.CSSProperties;
  /** Estilo do cabeçalho inteiro. */
  cardHeaderStyle?: React.CSSProperties;
  /** Conteúdo do card. */
  children?: React.ReactNode;
};

/**
 * Card com título, conteúdo e rodapé opcionais. Para um card que abre e fecha, use o `PanelCollapse`.
 * @import import Panel from 'src/componentes/panel';
 */
export default function Panel({
  header,
  children,
  subtitle,
  footer,
  className,
  headerStyle,
  cardHeaderStyle,
  classBody,
  classFooter,
  cardWidth,
  style,
  headerContent,
  headerAside,
  onHeaderRowClick,
  showInfo = false,
  idInfo = 'panelHeaderInfo',
  infoText = '',
  marginBottomCard = 15,
  ...rest
}: PanelProps) {
  const info = showInfo && (
    <Info id={idInfo} className="tw:ml-1" placement="right" text={infoText} />
  );

  return (
    <Card
      style={{ marginBottom: marginBottomCard, ...style, width: cardWidth }}
      className={cn('card', className)}
      {...rest}
    >
      {header && (
        <CardHeader className="card-header" style={cardHeaderStyle}>
          {headerAside ? (
            <div
              className={cn(
                'configuration-panel-header-row tw:flex tw:w-full tw:items-center tw:justify-between tw:gap-3',
                onHeaderRowClick && 'tw:cursor-pointer'
              )}
              onClick={
                onHeaderRowClick
                  ? (e) => {
                      const target = e.target as Element;
                      if (target.closest('[data-slot="tooltip-trigger"]'))
                        return;
                      onHeaderRowClick(e);
                    }
                  : undefined
              }
            >
              <CardTitle
                className="card-title tw:mb-0 tw:min-w-0 tw:flex-1"
                style={headerStyle}
              >
                {header}
                {info}
              </CardTitle>
              {headerAside}
            </div>
          ) : (
            <CardTitle className="card-title" style={headerStyle}>
              {header}
              {info}
            </CardTitle>
          )}
          {subtitle && (
            <CardDescription className="tw:mt-2">{subtitle}</CardDescription>
          )}
          {headerContent}
        </CardHeader>
      )}
      <CardContent className={cn('card-body', classBody)}>
        {children}
      </CardContent>
      {footer && (
        <CardFooter className={cn('card-footer', classFooter)}>
          {typeof footer === 'function' ? footer() : footer}
        </CardFooter>
      )}
    </Card>
  );
}
